import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ApiEngine from "../../util/ApiEngine";
import { ApiKey, ApiUrl } from "../../util/Constant";
import { setBusy, setIdle, showResponseMessage } from "../../redux/AppAction";
import { startGame } from "../../redux/GameAction";
import {
  SurveyQuestionType,
  SURVEY_MAX_IMAGE_BYTES,
  SURVEY_IMAGE_TYPES,
  isChoiceQuestion,
  acceptsImages,
  getTextMaxLength,
  getOtherMaxLength,
  getMaxImages,
  createResolver,
  buildSectionSteps,
  getSectionTitle,
  getQuestionGroupProgress,
  validateQuestion,
  buildAnswerPayload,
} from "./surveyLogic";

const SCREEN_INTRO = "intro";
const SCREEN_STAGE = "stage";
const SCREEN_THANKS = "thanks";
const SCREEN_COMPLETED = "completed";
const SURVEY_MONSTER_URL = "https://cdn.i8global.com/i8app/survey/survey_monster.png";
const SURVEY_SUCCESS_URL = "/img/survey/survey-success.png";
const SURVEY_BRAND_LOGO_URL = "/img/i8.live_2026.png";

const SurveyModal = ({ isOpen, survey, platform, onClose, onSubmitted }) => {
  const { t, i18n } = useTranslation();
  const _dispatch = useDispatch();
  const bodyRef = useRef(null);

  const campaign = survey.campaign;
  const questions = survey.questions || [];
  const [screen, setScreen] = useState(survey.submitted ? SCREEN_COMPLETED : SCREEN_INTRO);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tncOpen, setTncOpen] = useState(false);
  const [gameListOpen, setGameListOpen] = useState(false);

  const resolver = useMemo(() => createResolver(questions, answers), [questions, answers]);
  /// One page = one currently visible question (adaptive show/hide still applies).
  const visibleQuestions = useMemo(
    () => questions.filter((question) => resolver.isVisible(question.id)),
    [questions, resolver]
  );
  /// Stepper steps come only from SurveyQuestion.SectionCode (distinct, first-seen order).
  const sectionSteps = useMemo(() => buildSectionSteps(questions), [questions]);
  const safeQuestionIndex = Math.min(questionIndex, Math.max(visibleQuestions.length - 1, 0));
  const currentQuestion = visibleQuestions[safeQuestionIndex];
  const isLastQuestion = safeQuestionIndex >= visibleQuestions.length - 1;

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [screen, safeQuestionIndex, currentQuestion && currentQuestion.id]);

  useEffect(() => {
    if (screen !== SCREEN_STAGE || visibleQuestions.length === 0) {
      return;
    }
    if (questionIndex > visibleQuestions.length - 1) {
      setQuestionIndex(visibleQuestions.length - 1);
    }
  }, [screen, questionIndex, visibleQuestions.length]);

  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => {
    return () => Object.values(filesRef.current).flat().forEach((item) => URL.revokeObjectURL(item.preview));
  }, []);

  function updateAnswer(questionId, changes) {
    setAnswers((previous) => ({ ...previous, [questionId]: { ...(previous[questionId] || {}), ...changes } }));
    setErrors((previous) => {
      const next = { ...previous };
      delete next[questionId];
      return next;
    });
  }

  function toggleOption(question, option) {
    const current = resolver.selectedOf(question.id);
    if (question.questionType === SurveyQuestionType._SINGLE_CHOICE) {
      updateAnswer(question.id, { optionIds: [option.id] });
      return;
    }
    if (current.includes(option.id)) {
      updateAnswer(question.id, { optionIds: current.filter((id) => id !== option.id) });
    } else if (!question.maxSelect || current.length < question.maxSelect) {
      updateAnswer(question.id, { optionIds: [...current, option.id] });
    }
  }

  function addImages(question, fileList) {
    const existing = files[question.id] || [];
    const room = getMaxImages(question) - existing.length;
    const picked = Array.from(fileList || []);
    const valid = picked.filter((file) => SURVEY_IMAGE_TYPES.includes(file.type) && file.size <= SURVEY_MAX_IMAGE_BYTES);
    if (valid.length < picked.length) {
      _dispatch(showResponseMessage(false, "SURVEY_IMAGE_INVALID"));
    }
    const added = valid.slice(0, Math.max(room, 0)).map((file) => ({ file, preview: URL.createObjectURL(file) }));
    if (added.length === 0) {
      return;
    }
    setFiles((previous) => ({ ...previous, [question.id]: [...existing, ...added] }));
    setErrors((previous) => {
      const next = { ...previous };
      delete next[question.id];
      return next;
    });
  }

  function removeImage(question, index) {
    const existing = files[question.id] || [];
    URL.revokeObjectURL(existing[index].preview);
    setFiles((previous) => ({ ...previous, [question.id]: existing.filter((_, i) => i !== index) }));
  }

  function goNext() {
    const questionErrors = validateQuestion(currentQuestion, answers, files, resolver);
    setErrors(questionErrors);
    if (Object.keys(questionErrors).length > 0) {
      return;
    }
    if (isLastQuestion) {
      submit();
    } else {
      setQuestionIndex(safeQuestionIndex + 1);
    }
  }

  function goBack() {
    setErrors({});
    if (safeQuestionIndex === 0) {
      setScreen(SCREEN_INTRO);
    } else {
      setQuestionIndex(safeQuestionIndex - 1);
    }
  }

  async function submit() {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    _dispatch(setBusy());
    try {
      const formData = new FormData();
      formData.append("campaignId", campaign.id);
      formData.append("languageCode", i18n.language || "en");
      formData.append("platform", platform);
      formData.append("answers", JSON.stringify(buildAnswerPayload(questions, answers, resolver)));
      Object.keys(files).forEach((questionId) => {
        if (resolver.isVisible(Number(questionId))) {
          files[questionId].forEach((item) => formData.append("images_" + questionId, item.file, item.file.name));
        }
      });

      const responseJson = await ApiEngine.post(ApiUrl._API_SUBMIT_SURVEY, formData);
      if (responseJson[ApiKey._API_SUCCESS_KEY]) {
        setScreen(SCREEN_THANKS);
        onSubmitted && onSubmitted(responseJson[ApiKey._API_DATA_KEY]);
      } else if (responseJson[ApiKey._API_MESSAGE_KEY] === "SURVEY_ALREADY_SUBMITTED") {
        setScreen(SCREEN_COMPLETED);
        onSubmitted && onSubmitted(null);
      } else if (responseJson[ApiKey._API_MESSAGE_KEY]) {
        _dispatch(showResponseMessage(false, responseJson[ApiKey._API_MESSAGE_KEY]));
      }
    } finally {
      _dispatch(setIdle());
      setIsSubmitting(false);
    }
  }

  function renderOptionStars(score) {
    const count = Math.max(0, Math.min(5, Number(score) || 0));
    if (!count) {
      return null;
    }
    return (
      <span className="survey-option__stars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((star) => (
          <i key={star} className={"fas fa-star" + (star <= count ? " is-on" : "")} />
        ))}
      </span>
    );
  }

  function renderChoice(question) {
    const selected = resolver.selectedOf(question.id);
    const options = resolver.visibleOptions(question.id);
    const isMulti = question.questionType === SurveyQuestionType._MULTIPLE_CHOICE;
    const maxReached = isMulti && question.maxSelect && selected.length >= question.maxSelect;
    const otherPicked = options.some((option) => option.isOther && selected.includes(option.id));
    const answer = answers[question.id] || {};
    const desktopCols = Math.max(1, Math.min(4, Number(question.optionsPerRow) || 1));
    const mobileSetting = Number(question.optionsPerRowMobile);
    const mobileCols = mobileSetting >= 1 ? Math.max(1, Math.min(4, mobileSetting)) : Math.min(desktopCols, desktopCols > 1 ? 2 : 1);
    const isGrid = desktopCols > 1 || mobileCols > 1;

    return (
      <>
        <div
          className={"survey-options" + (isGrid ? " survey-options--grid" : "") + (isGrid && mobileCols === 1 ? " survey-options--stack" : "")}
          style={isGrid ? { "--survey-cols": desktopCols, "--survey-cols-mobile": mobileCols } : undefined}
        >
          {options.map((option) => {
            const isSelected = selected.includes(option.id);
            return (
              <button
                type="button"
                key={option.id}
                className={
                  "survey-option"
                  + (isSelected ? " survey-option--selected" : "")
                  + (isMulti ? " survey-option--multi" : "")
                  + (isGrid && !option.isOther ? " survey-option--tile" : "")
                  + (isGrid && option.isOther ? " survey-option--span" : "")
                }
                disabled={!isSelected && maxReached}
                onClick={() => toggleOption(question, option)}
              >
                {option.iconUrl
                  ? <img className="survey-option__icon" src={option.iconUrl} alt="" />
                  : renderOptionStars(option.score)}
                <span className="survey-option__label">{option.label}</span>
                <span className="survey-option__mark">{isSelected ? <i className="fas fa-check" /> : null}</span>
              </button>
            );
          })}
        </div>
        {otherPicked && (
          <input
            type="text"
            className="survey-input"
            placeholder={t("SURVEY_OTHER_PLACEHOLDER")}
            maxLength={getOtherMaxLength(question)}
            value={answer.otherText || ""}
            onChange={(e) => updateAnswer(question.id, { otherText: e.target.value })}
          />
        )}
      </>
    );
  }

  function renderText(question) {
    const answer = answers[question.id] || {};
    const maxLength = getTextMaxLength(question);
    const text = answer.text || "";
    return (
      <>
        {question.questionType === SurveyQuestionType._SHORT_TEXT ? (
          <input
            type="text"
            className="survey-input"
            placeholder={t("SURVEY_TEXT_PLACEHOLDER")}
            maxLength={maxLength}
            value={text}
            onChange={(e) => updateAnswer(question.id, { text: e.target.value })}
          />
        ) : (
          <textarea
            className="survey-input survey-input--textarea"
            placeholder={t("SURVEY_TEXT_PLACEHOLDER")}
            maxLength={maxLength}
            rows={4}
            value={text}
            onChange={(e) => updateAnswer(question.id, { text: e.target.value })}
          />
        )}
        <div className="survey-counter">{t("SURVEY_CHARACTERS", { count: text.length, max: maxLength })}</div>
      </>
    );
  }

  function renderRating(question) {
    const value = (answers[question.id] || {}).value || 0;
    return (
      <div className="survey-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            type="button"
            key={star}
            className={"survey-rating__star" + (star <= value ? " survey-rating__star--active" : "")}
            onClick={() => updateAnswer(question.id, { value: star })}
            aria-label={String(star)}
          >
            <i className="fas fa-star" />
          </button>
        ))}
      </div>
    );
  }

  function renderImages(question) {
    const list = files[question.id] || [];
    const maxImages = getMaxImages(question);
    return (
      <div className="survey-images">
        {list.map((item, index) => (
          <div className="survey-images__item" key={item.preview}>
            <img src={item.preview} alt="" />
            <button type="button" className="survey-images__remove" onClick={() => removeImage(question, index)} aria-label={t("SURVEY_REMOVE_IMAGE")}>
              <i className="fas fa-times" />
            </button>
          </div>
        ))}
        {list.length < maxImages && (
          <label className="survey-images__add">
            <i className="fas fa-camera" />
            <span>{t("SURVEY_UPLOAD_IMAGE")}</span>
            <input
              type="file"
              accept={SURVEY_IMAGE_TYPES.join(",")}
              multiple={maxImages - list.length > 1}
              onChange={(e) => {
                addImages(question, e.target.files);
                e.target.value = "";
              }}
            />
          </label>
        )}
        <div className="survey-hint survey-images__hint">{t("SURVEY_UPLOAD_HINT", { count: maxImages })}</div>
      </div>
    );
  }

  function renderQuestion(question) {
    let hint = null;
    if (question.questionType === SurveyQuestionType._SINGLE_CHOICE) {
      hint = t("SURVEY_SELECT_ONE");
    } else if (question.questionType === SurveyQuestionType._MULTIPLE_CHOICE) {
      hint = question.maxSelect ? t("SURVEY_SELECT_UP_TO", { count: question.maxSelect }) : t("SURVEY_SELECT_ALL");
    } else if (question.questionType === SurveyQuestionType._RATING) {
      hint = t("SURVEY_SELECT_ONE");
    }
    return (
      <div className={"survey-question" + (errors[question.id] ? " survey-question--error" : "")} key={question.id} data-question-id={question.id}>
        <div className="survey-question__title">
          {question.title}
          {!question.isRequired && <span className="survey-question__optional">{t("SURVEY_OPTIONAL")}</span>}
        </div>
        {question.description && <div className="survey-question__description">{question.description}</div>}
        {hint && <div className="survey-hint">{hint}</div>}
        {errors[question.id] && (
          <div className="survey-question__error">{t(errors[question.id], { count: question.maxSelect })}</div>
        )}
        <div className="survey-question__body" ref={bodyRef}>
          {isChoiceQuestion(question) && renderChoice(question)}
          {(question.questionType === SurveyQuestionType._SHORT_TEXT || question.questionType === SurveyQuestionType._LONG_TEXT) && renderText(question)}
          {question.questionType === SurveyQuestionType._RATING && renderRating(question)}
          {acceptsImages(question) && renderImages(question)}
        </div>
      </div>
    );
  }

  function startSurvey() {
    setQuestionIndex(0);
    setScreen(SCREEN_STAGE);
  }

  function renderSectionStepper(activeIndex = 0, allDone = false) {
    if (sectionSteps.length === 0) {
      return null;
    }
    return (
      <div className={"survey-stepper" + (allDone ? " survey-stepper--complete" : "")} aria-hidden="true">
        {sectionSteps.map((step, index) => (
          <div
            key={step.key}
            className={
              "survey-stepper__item"
              + (index === activeIndex ? " is-active" : "")
              + ((allDone || index < activeIndex) ? " is-done" : "")
            }
          >
            <div className="survey-stepper__node">{index + 1}</div>
            <div className="survey-stepper__label">{getSectionTitle(step.code, questions, t, i18n)}</div>
          </div>
        ))}
      </div>
    );
  }

  function freeSpinRecord() {
    return campaign.freeSpin || campaign.FreeSpin || {};
  }

  function freeSpinCountValue() {
    const spin = freeSpinRecord();
    const raw = spin.freeSpinCount ?? spin.FreeSpinCount ?? spin.chipsNumber ?? spin.ChipsNumber;
    const count = Number(raw);
    return Number.isFinite(count) && count > 0 ? count : null;
  }

  function freeSpinIsChip() {
    const spin = freeSpinRecord();
    if (spin.isChip || spin.IsChip) {
      return true;
    }
    return /chip/i.test(String(spin.bonusType || spin.BonusType || ""));
  }

  function freeSpinTitle() {
    const count = freeSpinCountValue();
    if (!count) {
      return t("SURVEY_INTRO_FREE_SPINS_TITLE");
    }
    return t(freeSpinIsChip() ? "SURVEY_GOLDEN_CHIP_COUNT" : "SURVEY_FREE_SPIN_COUNT", { count });
  }

  function freeSpinProviderName() {
    const spin = freeSpinRecord();
    return spin.productName || spin.ProductName || "";
  }

  function freeSpinProviderText() {
    const parts = [];
    const product = freeSpinProviderName();
    if (product) {
      parts.push(product);
    }
    const spin = freeSpinRecord();
    const stake = spin.betValue ?? spin.BetValue ?? spin.totalBet ?? spin.TotalBet ?? spin.chipValue ?? spin.ChipValue;
    if (stake != null && stake !== "") {
      parts.push("RM" + stake);
    }
    return parts.join(" · ");
  }

  function freeSpinGames() {
    const spin = freeSpinRecord();
    const games = spin.games || spin.Games;
    return Array.isArray(games) ? games : [];
  }

  function freeSpinGameTitle() {
    const spin = freeSpinRecord();
    const product = spin.productName || spin.ProductName || spin.name || "";
    const kind = freeSpinIsChip() ? "Free Chips" : "Free Spin";
    const count = freeSpinCountValue();
    const stake = spin.betValue ?? spin.BetValue ?? spin.totalBet ?? spin.TotalBet ?? spin.chipValue ?? spin.ChipValue;
    const amount = count ? count + (stake != null && stake !== "" ? " x RM" + stake : "") : "";
    return [product, kind, amount].filter(Boolean).join(" ");
  }

  function renderGameListLink(label) {
    if (freeSpinGames().length === 0) {
      return <span>{label}</span>;
    }
    return (
      <button type="button" className="survey-games-link" onClick={() => setGameListOpen(true)}>
        {label}
      </button>
    );
  }

  function renderIntroRewards(variant) {
    const tnc = (campaign.tnc || "").trim();
    const providerName = freeSpinProviderName();
    return (
      <div className={"survey-rewards survey-rewards--" + variant}>
        <div className="survey-intro-rewards">
          <div className="survey-intro-rewards__row">
            <span className="survey-intro-rewards__gift" aria-hidden="true">🎁</span>
            <div className="survey-intro-rewards__copy">
              <div className="survey-intro-rewards__headline">
                <b>RM{campaign.rewardAmount}</b>
                {campaign.hasFreeSpin && (
                  <>
                    {" + "}
                    <b>{freeSpinTitle()}</b>
                  </>
                )}
              </div>
              <div className="survey-intro-rewards__sub">
                {campaign.hasFreeSpin
                  ? (providerName ? renderGameListLink(providerName) : t("SURVEY_INTRO_REWARD_SUB_WITH_SPINS"))
                  : t("SURVEY_INTRO_FREE_CREDIT_LABEL")}
              </div>
            </div>
          </div>
          {campaign.implementedRewardAmount > 0 && (
            <div className="survey-intro-rewards__extra">
              <span className="survey-intro-rewards__crown" aria-hidden="true">👑</span>
              <span>{t("SURVEY_INTRO_IMPLEMENTED_HINT", { amount: campaign.implementedRewardAmount })}</span>
            </div>
          )}
        </div>
        {tnc && (
          <button type="button" className="survey-intro-rewards__tnc" onClick={() => setTncOpen(true)} aria-label={t("SURVEY_TNC")}>
            <span aria-hidden="true">i</span>
          </button>
        )}
      </div>
    );
  }

  function renderTncPopup() {
    const tnc = (campaign.tnc || "").trim();
    if (!tncOpen || !tnc) {
      return null;
    }
    const isHtml = /<[a-z][\s\S]*>/i.test(tnc);
    return (
      <div className="survey-tnc" role="dialog" aria-modal="true" aria-label={t("SURVEY_TNC_TITLE")}>
        <button type="button" className="survey-tnc__backdrop" onClick={() => setTncOpen(false)} aria-label={t("SURVEY_CLOSE")} />
        <div className="survey-tnc__panel">
          <div className="survey-tnc__header">
            <h3>{t("SURVEY_TNC_TITLE")}</h3>
            <button type="button" className="survey-modal__close" onClick={() => setTncOpen(false)} aria-label={t("SURVEY_CLOSE")}>
              <i className="fas fa-times" />
            </button>
          </div>
          {isHtml
            ? <div className="survey-tnc__body survey-content" dangerouslySetInnerHTML={{ __html: tnc }} />
            : <div className="survey-tnc__body survey-content survey-content--plain">{tnc}</div>}
        </div>
      </div>
    );
  }

  function startSurveyGame(game) {
    if (!game || !game.id) {
      return;
    }
    _dispatch(startGame({
      id: game.id,
      gameName: game.gameName || game.name,
      gameUm: game.gameUm === true,
      productId: game.productId,
      isApp: !!game.isApp,
      appUrl: game.appUrl,
      appDeepLink: game.appDeepLink,
      packageNameKeyword: game.packageNameKeyword,
      isNewWindow: !!game.isNewWindow,
      product: game.product || {
        id: game.productId,
        productName: game.productName,
        isSeamless: true,
        hasOwnLobby: false,
      },
      productCategory: game.productCategory || { originalTitle: "Slots" },
    }));
  }

  function renderGameListPopup() {
    const games = freeSpinGames();
    if (!gameListOpen || games.length === 0) {
      return null;
    }
    return (
      <div className="survey-games" role="dialog" aria-modal="true" aria-label={freeSpinGameTitle()}>
        <button type="button" className="survey-games__backdrop" onClick={() => setGameListOpen(false)} aria-label={t("CLOSE")} />
        <div className="survey-games__panel">
          <h3>{freeSpinGameTitle()}</h3>
          <div className="survey-games__list">
            {games.map((game) => (
              <button type="button" className="survey-games__card" key={game.id || game.name} onClick={() => startSurveyGame(game)}>
                {game.image
                  ? <img src={game.image} alt="" />
                  : <div className="survey-games__image survey-games__image--empty" />}
                <div className="survey-games__name">{game.name}</div>
                {game.productName && <div className="survey-games__product">{game.productName}</div>}
              </button>
            ))}
          </div>
          <button type="button" className="survey-games__close" onClick={() => setGameListOpen(false)}>
            {t("CLOSE")}
          </button>
        </div>
      </div>
    );
  }

  function renderHeroPanel() {
    return (
      <div className="survey-intro__hero">
        <div className="survey-intro__monster-wrap">
          <img src={SURVEY_MONSTER_URL} alt="" className="survey-intro__monster" />
        </div>
        <h3 className="survey-intro__hero-title">
          <span>{t("SURVEY_INTRO_HERO_LINE1")}</span>{" "}
          <em>{t("SURVEY_INTRO_HERO_LINE2")}</em>
        </h3>
        <p className="survey-intro__hero-text">{t("SURVEY_INTRO_HERO_BODY")}</p>
        {renderIntroRewards("panel")}
      </div>
    );
  }

  function renderStartButton() {
    return (
      <button
        type="button"
        className="survey-btn survey-btn--primary survey-intro__start"
        disabled={visibleQuestions.length === 0}
        onClick={startSurvey}
      >
        {t("SURVEY_START")} <span aria-hidden="true">&gt;</span>
      </button>
    );
  }

  function renderShell(options) {
    const { activeStep, allDone, shellClass, panel, footer } = options;
    return (
      <div className={"survey-intro" + (shellClass ? " " + shellClass : "")}>
        <div className="survey-intro__header">
          <img className="survey-intro__brand-img" src={SURVEY_BRAND_LOGO_URL} alt="i8.live" />
          <div className="survey-intro__heading">
            <h2>
              {t("SURVEY_INTRO_TITLE")}
              {campaign.isTest && <span className="survey-modal__test">{t("SURVEY_TEST_BADGE")}</span>}
            </h2>
            <p>{t("SURVEY_INTRO_SUBTITLE")}</p>
          </div>
          <button type="button" className="survey-modal__close" onClick={onClose} aria-label={t("SURVEY_CLOSE")}>
            <i className="fas fa-times" />
          </button>
        </div>
        {renderIntroRewards("banner")}
        {renderSectionStepper(activeStep, allDone)}
        <div className="survey-intro__grid">
          {renderHeroPanel()}
          <div className="survey-intro__panel">{panel}</div>
        </div>
        {footer ? <div className="survey-intro__footer">{footer}</div> : null}
      </div>
    );
  }

  function renderIntro() {
    return renderShell({
      activeStep: 0,
      allDone: false,
      shellClass: "survey-shell--intro",
      footer: renderStartButton(),
      panel: (
        <>
          <div className="survey-intro__welcome">{t("SURVEY_INTRO_WELCOME")}</div>
          <h3>{t("SURVEY_INTRO_VOICE_TITLE")}</h3>
          <p>{t("SURVEY_INTRO_VOICE_BODY")}</p>
          <div className="survey-intro__info">
            <span className="survey-intro__info-icon" aria-hidden="true">i</span>
            <p>{t("SURVEY_INTRO_ADAPTIVE_HINT")}</p>
          </div>
          <div className="survey-intro__start-slot">{renderStartButton()}</div>
        </>
      ),
    });
  }

  function renderThanksReward() {
    return (
      <div className="survey-thanks-reward">
        <div className="survey-thanks-reward__row">
          <div className="survey-thanks-reward__item">
            <span className="survey-thanks-reward__icon" aria-hidden="true">🪙</span>
            <div className="survey-thanks-reward__copy">
              <b>RM{campaign.rewardAmount}</b>
              <span>{t("SURVEY_INTRO_FREE_CREDIT_LABEL")}</span>
            </div>
          </div>
          {campaign.hasFreeSpin && (
            <>
              <span className="survey-thanks-reward__plus" aria-hidden="true">+</span>
              <div className="survey-thanks-reward__item">
                <span className="survey-thanks-reward__icon" aria-hidden="true">🎁</span>
                <div className="survey-thanks-reward__copy">
                  <b>{freeSpinTitle()}</b>
                  {freeSpinProviderText()
                    ? renderGameListLink("(" + freeSpinProviderText() + ")")
                    : <span>{t("SURVEY_INTRO_FREE_SPINS_SUB")}</span>}
                </div>
              </div>
            </>
          )}
        </div>
        {campaign.implementedRewardAmount > 0 && (
          <div className="survey-thanks-reward__extra">
            <span aria-hidden="true">👑</span>
            <span>{t("SURVEY_INTRO_IMPLEMENTED_HINT", { amount: campaign.implementedRewardAmount })}</span>
          </div>
        )}
      </div>
    );
  }

  function renderThanks() {
    const isFreshSubmit = screen === SCREEN_THANKS;
    return renderShell({
      activeStep: Math.max(sectionSteps.length - 1, 0),
      allDone: true,
      shellClass: "survey-shell--thanks",
      panel: (
        <>
          <div className="survey-thanks__eyebrow">{t("SURVEY_THANKS_EYEBROW")}</div>
          <img className="survey-thanks__success" src={SURVEY_SUCCESS_URL} alt="" />
          <div className="survey-thanks__badge">{t("SURVEY_THANKS_BADGE")}</div>
          <h3>{t("SURVEY_THANKS_TITLE")}</h3>
          <p>{isFreshSubmit ? t("SURVEY_THANKS_SUBTITLE") : t("SURVEY_ALREADY_COMPLETED")}</p>
          {renderThanksReward()}
          <div className="survey-intro__info">
            <span className="survey-intro__info-icon" aria-hidden="true">i</span>
            <p>{t("SURVEY_INTRO_ADAPTIVE_HINT")}</p>
          </div>
          <button type="button" className="survey-btn survey-btn--primary survey-intro__start" onClick={onClose}>
            {t("SURVEY_BACK_TO_I8")}
          </button>
        </>
      ),
    });
  }

  function renderStage() {
    if (!currentQuestion) {
      return null;
    }
    const stageKey = String(currentQuestion.sectionCode || "GENERAL").toUpperCase();
    let activeSectionIndex = sectionSteps.findIndex((step) => step.key === stageKey);
    if (activeSectionIndex < 0) {
      activeSectionIndex = 0;
    }
    const sectionLabel = getSectionTitle(
      (sectionSteps[activeSectionIndex] && sectionSteps[activeSectionIndex].code) || currentQuestion.sectionCode,
      questions,
      t,
      i18n
    );
    const progress = getQuestionGroupProgress(currentQuestion, questions);

    return renderShell({
      activeStep: activeSectionIndex,
      allDone: false,
      shellClass: "survey-shell--stage",
      panel: (
        <>
          <div className="survey-stage-panel">
            <div className="survey-question-meter">
              <div className="survey-stage-panel__eyebrow">
                {progress.code ? progress.code + " / " : ""}
                {String(sectionLabel || stageKey).toUpperCase()}
              </div>
              {progress.showBar && (
                <div className="survey-question-meter__row">
                  <span className="survey-question-meter__label">
                    {t("SURVEY_QUESTION_OF", { current: progress.current, total: progress.total })}
                  </span>
                  <span className="survey-question-meter__track" aria-hidden="true">
                    <span className="survey-question-meter__fill" style={{ width: progress.percent + "%" }} />
                  </span>
                  <span className="survey-question-meter__percent">{progress.percent}%</span>
                </div>
              )}
            </div>
            {renderQuestion(currentQuestion)}
          </div>
          <div className="survey-stage-panel__nav">
            <button type="button" className="survey-btn survey-btn--ghost" onClick={goBack}>
              <span aria-hidden="true">&lt;</span> {t("SURVEY_BACK")}
            </button>
            <button type="button" className="survey-btn survey-btn--primary" disabled={isSubmitting} onClick={goNext}>
              {isLastQuestion ? t("SURVEY_SUBMIT") : t("SURVEY_NEXT")} <span aria-hidden="true">&gt;</span>
            </button>
          </div>
        </>
      ),
    });
  }

  return (
    <Modal
      className="survey-modal survey-modal--intro"
      contentClassName="survey-modal__content"
      isOpen={isOpen}
      toggle={onClose}
      centered
      fade={true}
    >
      <ModalBody>
        {screen === SCREEN_INTRO && renderIntro()}
        {screen === SCREEN_STAGE && renderStage()}
        {(screen === SCREEN_THANKS || screen === SCREEN_COMPLETED) && renderThanks()}
        {renderTncPopup()}
        {renderGameListPopup()}
      </ModalBody>
    </Modal>
  );
};

export default SurveyModal;
